import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import axiosInstance from "../../../variables/api/axiosInstance";

const AddEtablissement = ({
  show,
  handleClose,
  editMode,
  selectedEtablissement,
  etablissements,
  onAdd,
  onUpdate,
}) => {
  const [formData, setFormData] = useState({
    nom: "",
    adresse: "",
    telephone1: "",
    telephone2: "",
    typeEtablissement: "",
  });

  const [loading, setLoading] = useState(false);
  const [typesEtablissement, setTypesEtablissement] = useState([]);

  useEffect(() => {
    axiosInstance
      .get(`/api/parametre/type/TYPE_ETABLISSEMENT`)
      .then((res) => {
        const validTypes = (res.data || []).filter(
          (t) => {
            const id = parseInt(t.parametreId ?? t.id, 10);
            return !Number.isNaN(id) && id > 0;
          }
        );
        setTypesEtablissement(validTypes);
      })
      .catch((err) => {
        console.error("Erreur lors du chargement des types d'établissement :", err);
        toast.error("Impossible de charger les types d'établissement");
      });
  }, []);

  useEffect(() => {
    if (editMode && selectedEtablissement) {
      const typeValue =
        selectedEtablissement.typeEtablissement?.parametreId ??
        selectedEtablissement.typeEtablissement?.id ??
        selectedEtablissement.typeEtablissement ??
        "";

      setFormData({
        nom: selectedEtablissement.nom || "",
        adresse: selectedEtablissement.adresse || "",
        telephone1: selectedEtablissement.contact?.[0] || "",
        telephone2: selectedEtablissement.contact?.[1] || "",
        typeEtablissement: typeValue,
      });
    } else {
      setFormData({
        nom: "",
        adresse: "",
        telephone1: "",
        telephone2: "",
        typeEtablissement: "",
      });
    }
  }, [editMode, selectedEtablissement]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const typeId =
      formData.typeEtablissement !== ""
        ? parseInt(formData.typeEtablissement, 10)
        : null;

    if (!typeId || Number.isNaN(typeId) || typeId <= 0) {
      toast.error("Sélectionnez un type d'établissement valide.");
      setLoading(false);
      return;
    }

    const selectedType = typesEtablissement.find((t) => {
      const currentId = parseInt(t.parametreId ?? t.id, 10);
      return currentId === typeId;
    });

    if (!selectedType) {
      toast.error("Type d'établissement introuvable ou non valide.");
      setLoading(false);
      return;
    }

    const payload = {
      ...(editMode && { codeId: selectedEtablissement?.codeId }), // uniquement en modification
      nom: formData.nom.trim(),
      adresse: formData.adresse.trim(),
      contact: [formData.telephone1, formData.telephone2].filter(Boolean),
      typeEtablissement: selectedType?.libelleParametre,
    };

    const isDuplicate = !editMode && etablissements?.some(
      (e) => e.nom.toLowerCase().trim() === payload.nom.toLowerCase()
    );
    if (isDuplicate) {
      toast.error("Cet établissement existe déjà.");
      setLoading(false);
      return;
    }

    const request = editMode
      ? axiosInstance.put(`/api/etablissement`, payload)
      : axiosInstance.post(`/api/etablissement`, payload);

    console.log("Payload envoyé au backend :", payload);

    request
      .then((res) => {
        toast.success(`Établissement ${editMode ? "modifié" : "ajouté"} avec succès!`);
        if (editMode && onUpdate) onUpdate(res.data);
        if (!editMode && onAdd) onAdd(res.data);
        handleClose();
      })
      .catch((err) => {
        console.error("Erreur lors de l'enregistrement :", err);
        toast.error("Erreur lors de l'enregistrement de l'établissement");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Body>
        <button
          type="button"
          className="btn-close btn-primary"
          onClick={handleClose}
          aria-label="Close"
        ></button>
        <div className="text-center mb-4">
          <h4>{editMode ? "Modifier l'établissement" : "Ajouter un établissement"}</h4>
        </div>
        <form className="row g-3" onSubmit={onSubmit}>
          <div className="col-12">
            <label className="form-label" htmlFor="nomField">Nom</label>
            <input
              type="text"
              id="nomField"
              name="nom"
              value={formData.nom}
              onChange={handleInputChange}
              className="form-control"
              required
            />
          </div>
          <div className="col-12">
            <label className="form-label" htmlFor="adresseField">Adresse</label>
            <input
              type="text"
              id="adresseField"
              name="adresse"
              value={formData.adresse}
              onChange={handleInputChange}
              className="form-control"
              required
            />
          </div>
          <div className="col-md-6">
            <label className="form-label" htmlFor="telephone1Field">Téléphone 1</label>
            <input
              type="text"
              id="telephone1Field"
              name="telephone1"
              value={formData.telephone1}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label" htmlFor="telephone2Field">Téléphone 2</label>
            <input
              type="text"
              id="telephone2Field"
              name="telephone2"
              value={formData.telephone2}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
          <div className="col-12">
            <label className="form-label" htmlFor="typeEtablissementField">Type d'établissement</label>
            <select
              id="typeEtablissementField"
              name="typeEtablissement"
              value={formData.typeEtablissement}
              onChange={handleInputChange}
              className="form-control"
              required
            >
              <option value="">Sélectionnez le type d'établissement</option>
              {typesEtablissement.map((type) => (
                <option
                  key={type.parametreId || type.id || type.libelleParametre}
                  value={(type.parametreId ?? type.id)?.toString()}
                >
                  {type.libelleParametre || type}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 text-center mt-4">
            <button type="submit" className="btn btn-primary me-3" disabled={loading}>
              {loading && <span className="spinner-grow spinner-grow-sm me-1"></span>}
              {loading ? "Enregistrement..." : "Enregistrer"}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              Annuler
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default AddEtablissement;
